import { inheritRequestCorrelation } from '@brighthub/structured-logging';
import { Injectable, inject } from '@angular/core';
import { ApiService, GenericResponseCode } from '@brighthub/api';
import { Observable, map } from 'rxjs';
import {
  CreateGoalRequest,
  GoalListPayload,
  GoalResource,
  QueryGoalsRequest,
  UpdateGoalRequest,
} from './goal-api.model';
import { GoalApi } from './goal-api.token';

interface GoalEnvelope<T> {
  result: { code: string; message?: string; status?: number };
  payload: T;
}

const PRIVATE_WRITE = { suppressRequestBodyTelemetry: true };

@Injectable()
export class HttpGoalApiService implements GoalApi {
  private readonly api = inject(ApiService);

  list(query: QueryGoalsRequest): Observable<GoalListPayload> {
    this.id(query.clientId);
    if (query.caseId !== undefined) this.id(query.caseId);
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined) params.set(key, String(value));
    });
    return this.api
      .get<GoalEnvelope<GoalListPayload>>('api-internal', `/goal?${params}`)
      .pipe(map((response) => this.payload(response)));
  }

  get(goalId: number): Observable<GoalResource> {
    return this.api
      .get<
        GoalEnvelope<GoalResource>
      >('api-internal', `/goal/${this.id(goalId)}`)
      .pipe(map((response) => this.payload(response)));
  }

  create(body: CreateGoalRequest): Observable<GoalResource> {
    this.id(body.clientId);
    if (body.caseId !== undefined) this.id(body.caseId);
    return this.api
      .post<
        GoalEnvelope<GoalResource>
      >('api-internal', '/goal', body, PRIVATE_WRITE)
      .pipe(map((response) => this.payload(response)));
  }

  update(goalId: number, body: UpdateGoalRequest): Observable<GoalResource> {
    return this.api
      .patch<
        GoalEnvelope<GoalResource>
      >('api-internal', `/goal/${this.id(goalId)}`, body, PRIVATE_WRITE)
      .pipe(map((response) => this.payload(response)));
  }

  link(goalId: number, caseId: number): Observable<void> {
    return this.changeLink(goalId, caseId, 'link');
  }

  unlink(goalId: number, caseId: number): Observable<void> {
    return this.changeLink(goalId, caseId, 'unlink');
  }

  private changeLink(
    goalId: number,
    caseId: number,
    operation: 'link' | 'unlink',
  ): Observable<void> {
    return this.api
      .post<
        GoalEnvelope<unknown>
      >('api-internal', `/goal/${this.id(goalId)}/${operation}`, { caseId: this.id(caseId) }, PRIVATE_WRITE)
      .pipe(
        map((response) => {
          // Link writes need no response resource; SERVICE_OK confirms success.
          this.checkSuccess(response);
        }),
      );
  }

  private id(value: number): number {
    if (!Number.isSafeInteger(value) || value <= 0) {
      throw new Error('A valid client, goal, or case identifier is required.');
    }
    return value;
  }

  private checkSuccess(response: GoalEnvelope<unknown>): void {
    if (response?.result?.code !== GenericResponseCode.SERVICE_OK) {
      throw inheritRequestCorrelation(
        Object.assign(
          new Error(response?.result?.message || 'The Goal request failed.'),
          {
            code: response?.result?.code,
            status: response?.result?.status,
          },
        ),
        response,
      );
    }
  }

  private payload<T>(response: GoalEnvelope<T>): T {
    this.checkSuccess(response);
    if (response.payload == null)
      throw new Error('Goal API returned no payload.');
    return response.payload;
  }
}
